import { isOpenableUrl } from '@littlebird/ui-utils';
import { find as findLinks } from 'linkifyjs';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Popover } from '@/components/ui/popover';
import { classList } from '@/utils/cn';

import { IconButton } from '../../IconButton';
import { Input } from '../../Input';
import { useEditorFloatingContainer } from '../base-editor-context';
import { useEditorState } from '../components';

function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (_error) {
    return false;
  }
}

function getUrlFromString(str: string) {
  str = str.trim();
  if (isValidUrl(str)) return isOpenableUrl(str) ? str : null;
  try {
    if (str.includes('.') && !str.includes(' ')) {
      return new URL(`https://${str}`).toString();
    }
  } catch (_error) {
    return null;
  }
}

interface LinkSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LinkSelector = ({
  open,
  onOpenChange: setOpen,
}: LinkSelectorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const editor = useEditorState();
  const container = useEditorFloatingContainer();

  if (!editor) return null;

  const href = editor.getAttributes('link').href;
  const { from, to } = editor.state.selection;
  const selectedLink = findLinks(
    editor.state.doc.textBetween(from, to, ' ')
  ).find((link) => link.type === 'url');
  const selectedUrl = selectedLink
    ? getUrlFromString(selectedLink.value)
    : undefined;

  return (
    <Popover.Root modal={true} open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <IconButton
          icon={Icons.link}
          aria-label="Add link"
          size="xs"
          variant="ghost"
          className={classList('hover:bg-surface-active', {
            'text-blue-500': editor.isActive('link'),
          })}
          tooltip="Add link"
          tooltipOptions={{ hotkey: ['meta', 'shift', 'k'], sideOffset: 9 }}
          onPointerDown={(e) => {
            if (open) e.stopPropagation();
          }}
        />
      </Popover.Trigger>
      <Popover.Content
        container={container}
        portalled
        align="start"
        className="w-60 p-0"
        sideOffset={12}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          editor.chain().focus().run();
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!editor.isEditable) return;
            const url = getUrlFromString(inputRef.current?.value ?? '');
            if (!url) {
              inputRef.current?.setCustomValidity(
                'Enter an http or https link.'
              );
              inputRef.current?.reportValidity();
              return;
            }
            if (
              url &&
              editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run()
            ) {
              setOpen(false);
            }
          }}
          className="flex items-center gap-1 p-1"
        >
          <Input
            ref={inputRef}
            type="text"
            placeholder="Paste a link"
            onChange={(event) => event.currentTarget.setCustomValidity('')}
            className={classList(
              'px-1.5 py-1',
              'h-6',
              'rounded-md smoothing-default',
              'focus:outline-none shadow-none',
              'w-full'
            )}
            defaultValue={href || selectedUrl || ''}
          />
          <IconButton
            size="xs"
            type={href ? 'button' : 'submit'}
            variant="ghost"
            strokeWidth={1}
            aria-label={href ? 'Remove link' : 'Save link'}
            icon={href ? Icons.trash : Icons.check}
            onClick={
              href
                ? () => {
                    if (!editor.isEditable) return;
                    editor
                      .chain()
                      .focus()
                      .extendMarkRange('link')
                      .unsetLink()
                      .run();
                    setOpen(false);
                  }
                : undefined
            }
          />
        </form>
      </Popover.Content>
    </Popover.Root>
  );
};
